import { DestroyRef, inject, Injectable } from '@angular/core';
import { AdministrationInfo, BalanceProviderInfo, EnvironmentInfo, IdentityProviderStatus, PmClient } from '@snelstart-ventures/fiscaal/domain-logic';
import { BehaviorSubject, catchError, combineLatest, distinctUntilChanged, EMPTY, map, Observable, of, shareReplay, Subject, take, tap } from 'rxjs';
import { SelectFormControlItem } from '@snelstart/ui-form-controls';
import { startWith, switchMap } from 'rxjs/operators';

@Injectable()
export class BalanceProviderService {
    private readonly _client = inject(PmClient);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _identityProviderChannel = new BroadcastChannel('identity-provider-authorize');

    constructor() {
        this._identityProviderChannel.onmessage = (event) => {
            if (event.data?.type === 'authorize-success') {
                console.debug('Received authorization success message, updating Identity Provider Status');
                this._refreshIdentityProviderStatus$.next();
            }
        };

        this._destroyRef.onDestroy(() => {
            console.debug('Closing Identity Provider Channel');
            this._identityProviderChannel.close();
        });
    }

    public readonly balanceProviders$: Observable<BalanceProviderInfo[]> = this._client.getBalanceProviders$().pipe(
        catchError((error: Error) => {
            console.error('BalanceProviderService: Error loading Balance Providers.', error);
            return of([] as BalanceProviderInfo[]);
        }),
        tap((balanceProviders) => {
            if (balanceProviders.length === 0) {
                console.debug('BalanceProviderService: No Balance Providers loaded');
            }

            if (balanceProviders.length > 0) {
                console.debug(`BalanceProviderService: Loaded ${balanceProviders.length} Balance Providers`);
            }
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public readonly balanceProviderOptions$ = this.balanceProviders$.pipe(
        map((balanceProviders) =>
            balanceProviders.map<SelectFormControlItem<string>>(
                (balanceProvider) =>
                    ({
                        value: balanceProvider.id,
                        label: balanceProvider.name,
                        disabled: balanceProvider.id === 'twinfield'
                    }) as SelectFormControlItem<string>,
            ),
        ),
        tap((balanceProviderOptions) => {
            console.debug(
                `BalanceProviderService: Mapped ${balanceProviderOptions.length} Balance Provider Options`,
                balanceProviderOptions,
            );
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    private _selectedBalanceProviderId$ = new BehaviorSubject<string | null>(null);

    public selectBalanceProvider(value: BalanceProviderInfo | string | null): void {
        this._selectedBalanceProviderId$.next(typeof value === 'string' ? value : (value?.id ?? null));
    }

    public readonly selectedBalanceProvider$: Observable<BalanceProviderInfo | null> = combineLatest([
        this._selectedBalanceProviderId$,
        this.balanceProviders$,
    ]).pipe(
        map(([selectedBalanceProviderId, balanceProviders]) =>
            balanceProviders != null
                ? (balanceProviders.find((balanceProvider) => balanceProvider.id === selectedBalanceProviderId) ?? null)
                : null,
        ),
        distinctUntilChanged(),
        tap((balanceProvider) => {
            if (balanceProvider != null) {
                console.debug(`BalanceProviderService: Selected ${balanceProvider.id} Balance Provider`);
            } else {
                console.debug('BalanceProviderService: No Balance Provider selected');
            }
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public readonly identityProvider$ = this.selectedBalanceProvider$.pipe(
        switchMap((selectedBalanceProvider) =>
            selectedBalanceProvider != null ? this._client.getIdentityProvider$(selectedBalanceProvider.id) : of(null),
        ),
        distinctUntilChanged(
            (previousIdentityProvider, currentIdentityProvider) => currentIdentityProvider?.id === previousIdentityProvider?.id,
        ),
        tap((identityProvider) => {
            if (identityProvider) {
                console.debug(`BalanceProviderService: Loaded "${identityProvider.name}" Identity Provider`, identityProvider);
            } else {
                console.debug('BalanceProviderService: No Identity Provider loaded');
            }
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    private _identityProviderEnvironmentId$ = new BehaviorSubject<string | null>(null);

    public setIdentityProviderEnvironmentId(value: string | null): void {
        this._identityProviderEnvironmentId$.next(value);
    }

    private _identityProviderAccessToken$ = new BehaviorSubject<string | null>(null);

    public setIdentityProviderAccessToken(value: string | null): void {
        this._identityProviderAccessToken$.next(value);
    }

    private _refreshIdentityProviderStatus$ = new Subject<void>();

    public readonly identityProviderStatus$ = combineLatest([
        this.identityProvider$,
        this._identityProviderEnvironmentId$,
        this._refreshIdentityProviderStatus$.pipe(startWith(void 0)),
    ]).pipe(
        switchMap(([identityProvider, identityProviderEnvironmentId]) => {
            if (identityProvider != null) {
                if (identityProvider.requiresEnvironment) {
                    if (!identityProviderEnvironmentId) {
                        return of({
                            id: identityProvider.id,
                            name: identityProvider.name,
                            authenticated: false,
                        } as IdentityProviderStatus);
                    }

                    return this._client.getIdentityProviderEnvironmentStatus$(identityProvider.id, identityProviderEnvironmentId);
                }

                return this._client.getIdentityProviderStatus$(identityProvider.id);
            }

            return of(null);
        }),
        tap((identityProviderStatus) => {
            if (identityProviderStatus) {
                console.debug(
                    `BalanceProviderService: Loaded "${identityProviderStatus.name}" Identity Provider Status (Authenticated: ${identityProviderStatus.authenticated})`,
                    identityProviderStatus,
                );
            } else {
                console.debug('BalanceProviderService: No Identity Provider Status loaded');
            }
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public readonly balanceProviderEnvironments$ = combineLatest([this.selectedBalanceProvider$, this.identityProviderStatus$]).pipe(
        switchMap(([selectedBalanceProvider, identityProviderStatus]) => {
            if (identityProviderStatus?.authenticated && selectedBalanceProvider?.requiresEnvironmentSelection) {
                return this._client.getBalanceProviderEnvironments$(selectedBalanceProvider.id);
            }

            return of([] as EnvironmentInfo[]);
        }),
        tap((balanceProviderEnvironments) => {
            if (balanceProviderEnvironments.length === 0) {
                console.debug('BalanceProviderService: No Balance Provider Environments loaded');
            }

            if (balanceProviderEnvironments.length > 0) {
                console.debug(`BalanceProviderService: Loaded ${balanceProviderEnvironments.length} Balance Provider Environments`);
            }
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public readonly balanceProviderEnvironmentOptions$ = this.balanceProviderEnvironments$.pipe(
        map((balanceProviderEnvironments) =>
            balanceProviderEnvironments.map<SelectFormControlItem<string>>(
                (balanceProviderEnvironment) =>
                    ({
                        value: balanceProviderEnvironment.id,
                        label: balanceProviderEnvironment.name,
                    }) as SelectFormControlItem<string>,
            ),
        ),
        tap((options) => console.debug(`BalanceProviderService: Mapped ${options.length} Balance Provider Environment Options`, options)),
    );

    private _balanceProviderEnvironmentId$ = new BehaviorSubject<string | null>(null);

    public selectBalanceProviderEnvironment(value: EnvironmentInfo | string | null): void {
        this._balanceProviderEnvironmentId$.next(typeof value === 'string' ? value : (value?.id ?? null));
    }

    public readonly selectedBalanceProviderEnvironment$ = combineLatest([
        this.balanceProviderEnvironments$,
        this._balanceProviderEnvironmentId$,
    ]).pipe(
        map(
            ([balanceProviderEnvironments, selectedBalanceProviderEnvironmentId]) =>
                balanceProviderEnvironments.find(
                    (balanceProviderEnvironment) => balanceProviderEnvironment.id === selectedBalanceProviderEnvironmentId,
                ) ?? null,
        ),
        distinctUntilChanged(
            (previousBalanceProviderEnvironment, currentBalanceProviderEnvironment) =>
                previousBalanceProviderEnvironment?.id === currentBalanceProviderEnvironment?.id,
        ),
        tap((selectedBalanceProviderEnvironment) => {
            if (selectedBalanceProviderEnvironment != null) {
                console.debug(
                    `BalanceProviderService: Selected "${selectedBalanceProviderEnvironment.name}" Balance Provider Environment`,
                    selectedBalanceProviderEnvironment,
                );
            } else {
                console.debug('BalanceProviderService: No Balance Provider Environment selected');
            }
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public readonly balanceProviderAdministrations$ = combineLatest([
        this.selectedBalanceProvider$,
        this.identityProvider$,
        this.identityProviderStatus$,
        this.selectedBalanceProviderEnvironment$,
        this._identityProviderEnvironmentId$,
    ]).pipe(
        switchMap(
            ([balanceProvider, identityProvider, identityProviderStatus, balanceProviderEnvironment, identityProviderEnvironmentId]) => {
                if (balanceProvider && identityProvider && identityProviderStatus?.authenticated) {
                    if (balanceProvider.requiresAdministrationSelection) {
                        if (identityProvider.supportsAccessToken) {
                            if (balanceProvider.supportsMultipleEnvironments) {
                                if (balanceProvider.requiresEnvironmentSelection) {
                                    if (balanceProviderEnvironment) {
                                        return this._client.getBalanceProviderEnvironmentAdministrations$(
                                            balanceProvider.id,
                                            balanceProviderEnvironment.id,
                                        );
                                    }
                                } else {
                                    if (identityProviderStatus.environmentId) {
                                        return this._client.getBalanceProviderEnvironmentAdministrations$(
                                            balanceProvider.id,
                                            identityProviderStatus.environmentId,
                                        );
                                    }
                                    if (identityProviderEnvironmentId) {
                                        return this._client.getBalanceProviderEnvironmentAdministrations$(
                                            balanceProvider.id,
                                            identityProviderEnvironmentId,
                                        );
                                    }
                                }
                            }
                        }

                        if (identityProvider.supportsAuthorize) {
                            if (balanceProvider.supportsMultipleEnvironments) {
                                if (balanceProvider.requiresEnvironmentSelection) {
                                    if (balanceProviderEnvironment) {
                                        return this._client.getBalanceProviderEnvironmentAdministrations$(
                                            balanceProvider.id,
                                            balanceProviderEnvironment.id,
                                        );
                                    }
                                } else {
                                    if (identityProviderEnvironmentId) {
                                        return this._client.getBalanceProviderEnvironmentAdministrations$(
                                            balanceProvider.id,
                                            identityProviderEnvironmentId,
                                        );
                                    }
                                }
                            }
                        }
                    }

                    if (!balanceProvider.supportsMultipleEnvironments) {
                        return this._client.getBalanceProviderAdministrations$(balanceProvider.id);
                    }
                }

                return of([] as AdministrationInfo[]);
            },
        ),
        tap((administrations) => {
            if (administrations.length === 0) {
                console.debug('BalanceProviderService: No administrations loaded');
            }

            if (administrations.length > 0) {
                console.debug(`BalanceProviderService: Loaded ${administrations.length} administrations`);
            }
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public readonly balanceProviderAdministrationOptions$ = this.balanceProviderAdministrations$.pipe(
        map((balanceProviderAdministrations) =>
            balanceProviderAdministrations.map<SelectFormControlItem<string>>(
                (balanceProviderAdministration) =>
                    ({
                        value: balanceProviderAdministration.id,
                        label: balanceProviderAdministration.name,
                    }) as SelectFormControlItem<string>,
            ),
        ),
        tap((balanceProviderAdministrationOptions) =>
            console.debug(
                `BalanceProviderService: Mapped ${balanceProviderAdministrationOptions.length} Balance Provider administration options`,
                balanceProviderAdministrationOptions,
            ),
        ),
    );

    private _balanceProviderAdministrationId = new BehaviorSubject<string | null>(null);

    public selectBalanceProviderAdministration(value: AdministrationInfo | string | null): void {
        this._balanceProviderAdministrationId.next(typeof value === 'string' ? value : (value?.id ?? null));
    }

    public readonly selectedBalanceProviderAdministration$ = combineLatest([
        this.balanceProviderAdministrations$,
        this._balanceProviderAdministrationId,
    ]).pipe(
        map(
            ([balanceProviderAdministrations, selectedBalanceProviderAdministrationId]) =>
                balanceProviderAdministrations.find((administration) => administration.id === selectedBalanceProviderAdministrationId) ??
                null,
        ),
        tap((selectedBalanceProviderAdministration) => {
            if (selectedBalanceProviderAdministration != null) {
                console.debug(
                    `BalanceProviderService: Selected "${selectedBalanceProviderAdministration.name}" administration`,
                    selectedBalanceProviderAdministration,
                );
            } else {
                console.debug('BalanceProviderService: No administration selected');
            }
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public readonly canLoginBalanceProviderAuthorize$ = combineLatest([this.identityProvider$, this.identityProviderStatus$]).pipe(
        take(1),
        switchMap(([identityProvider, identityProviderStatus]) => {
            if (
                identityProvider == null ||
                !identityProvider.supportsAuthorize ||
                identityProviderStatus == null ||
                identityProviderStatus.authenticated
            ) {
                return of(false);
            }

            return of(true);
        }),
    );

    public readonly loginBalanceProviderAuthorize$ = combineLatest([this.identityProvider$, this.identityProviderStatus$]).pipe(
        take(1),
        switchMap(([identityProvider, identityProviderStatus]) => {
            if (
                identityProvider == null ||
                !identityProvider.supportsAuthorize ||
                identityProviderStatus == null ||
                identityProviderStatus.authenticated
            ) {
                return of();
            }

            return this._client.getIdentityProviderAuthorizeRequest$(identityProvider.id).pipe(
                take(1),
                tap((authorizeRequest) => {
                    console.debug(`Opening new window for "${identityProvider.name}" authorization.`);

                    const authorizeWindow = window.open(authorizeRequest.uri);

                    if (!authorizeWindow) {
                        console.error(`Error opening authorize window for ${identityProvider.name}`);
                    }
                }),
            );
        }),
    );

    public readonly canLoginBalanceProviderAccessToken$ = combineLatest([
        this.identityProvider$,
        this.identityProviderStatus$,
        this._identityProviderAccessToken$,
    ]).pipe(
        take(1),
        switchMap(([identityProvider, identityProviderStatus, accessToken]) => {
            if (
                identityProvider == null ||
                !identityProvider.supportsAccessToken ||
                identityProviderStatus == null ||
                identityProviderStatus.authenticated ||
                accessToken == null
            ) {
                return of(false);
            }

            return of(true);
        }),
    );

    public readonly loginBalanceProviderAccessToken$ = combineLatest([
        this.identityProvider$,
        this.identityProviderStatus$,
        this._identityProviderEnvironmentId$,
        this._identityProviderAccessToken$,
    ]).pipe(
        take(1),
        switchMap(([identityProvider, identityProviderStatus, identityProviderEnvironmentId, accessToken]) => {
            if (
                identityProvider == null ||
                !identityProvider.supportsAccessToken ||
                identityProviderStatus == null ||
                identityProviderStatus.authenticated ||
                accessToken == null
            ) {
                return EMPTY;
            }

            if (identityProviderEnvironmentId == null) {
                identityProviderEnvironmentId = crypto.randomUUID();
                this._identityProviderEnvironmentId$.next(identityProviderEnvironmentId);
            }

            return this._client.postIdentityProviderAuthorizeEnvironment$(identityProvider.id, identityProviderEnvironmentId, accessToken);
        }),
        tap(() => {
            console.debug(`Authorized Identity Provider access token`);
            this._refreshIdentityProviderStatus$.next();
        }),
        catchError((error) => {
            console.error(`Error authorizing Identity Provider access token`, error);
            this._identityProviderEnvironmentId$.next(null);
            throw error;
        }),
    );

    public readonly canLogoffBalanceProvider$ = combineLatest([this.identityProvider$, this.identityProviderStatus$]).pipe(
        take(1),
        switchMap(([identityProvider, identityProviderStatus]) => {
            if (
                identityProvider == null ||
                identityProviderStatus == null ||
                !identityProviderStatus.authenticated ||
                (identityProvider.requiresEnvironment && identityProviderStatus.environmentId == null)
            ) {
                return of(false);
            }

            return of(true);
        }),
    );

    public readonly logoffBalanceProvider$ = combineLatest([this.identityProvider$, this.identityProviderStatus$]).pipe(
        take(1),
        switchMap(([identityProvider, identityProviderStatus]) => {
            if (identityProvider != null && identityProviderStatus != null) {
                if (identityProvider.requiresEnvironment) {
                    return this._client.postIdentityProviderEnvironmentEndSession$(
                        identityProvider.id,
                        identityProviderStatus.environmentId,
                    );
                } else {
                    return this._client.postIdentityProviderEndSession$(identityProvider.id);
                }
            }
            return of();
        }),
        tap(() => this._refreshIdentityProviderStatus$.next()),
    );
}
